import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationStart } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Button, InputForm, SelectForm } from 'ustore-ui';

import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import {
  IndicationService,
  IndicationRequest,
  ChannelIndicationRequest,
} from '../../core/services/indication.service';
import { IndicationChannelHelper } from '../../core/services/indication-channel.helper';
import { ChannelConfigService } from '../../core/services/channel-config.service';
import { ToastService } from '../../shared/services/toast.service';
import { VideoStateService } from '../../shared/services/video-state.service';
import { CustomValidators } from '../../shared/validators';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { formatCnpj, unformatCnpj } from '../../core/utils/cnpj.util';

export const DEFAULT_INDICATION_REGULATION_URL =
  'https://assets-mktplace-001.s3.sa-east-1.amazonaws.com/Regulamento-A%C3%A7%C3%A3o-Indica%C3%A7ao-PME.pdf';

interface IBGECity {
  id: number;
  nome: string;
  microrregiao?: any;
  'regiao-imediata'?: any;
}

@Component({
  selector: 'umkt-indication',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    Button,
    InputForm,
    SelectForm,
    CustomInputComponent,
    I18nPipe
],
  templateUrl: './indication.component.html',
  styleUrls: ['./indication.component.scss'],
})
export class IndicationComponent implements OnInit, AfterViewInit, OnDestroy {
  indicationForm: FormGroup;
  isSubmitting = false;
  isUtmMode = false;
  cities: string[] = [];
  filteredCities: string[] = [];
  isLoadingCities = false;
  @ViewChild('headerVideo') headerVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('headerVideoMobile') headerVideoMobile?: ElementRef<HTMLVideoElement>;
  showSoundOverlay = false;
  desktopBannerFailed = false;
  mobileBannerFailed = false;
  isScrolled = false;
  private scrollThreshold = 100; // pixels to scroll before floating
  private floatingDismissed = false; // close X disables floating until reload
  private scrollHandler?: () => void; 
  private desktopVideoListeners: [string, EventListener][] = [];
  private mobileVideoListeners: [string, EventListener][] = [];
  private visibilityChangeHandler?: () => void;
  private programmaticPause = false; // do not record as user pause
  
  // Subject para gerenciar subscriptions
  private destroy$ = new Subject<void>();
  selectedState = '';
  showCitySuggestions = false;
  selectedSuggestionIndex = -1;

  brazilStates = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ];
  private formBuilder = inject(FormBuilder);
  private indicationService = inject(IndicationService);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private videoState = inject(VideoStateService);
  private i18nService = inject(I18nService);
  private indicationChannelHelper = inject(IndicationChannelHelper);
  private channelConfigService = inject(ChannelConfigService);

  constructor() {
    this.indicationForm = this.formBuilder.group({
      referrerName: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      referrerEmail: ['', [Validators.required, Validators.email, CustomValidators.emailNoSpaces, CustomValidators.corporateEmail]],
      referrerPhone: ['', [Validators.required, CustomValidators.phone]],
      referrerArea: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      referrerCity: ['', [Validators.required, CustomValidators.noWhitespaceOnly]],
      referrerState: [''],
      contactName: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      companyName: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      companyCNPJ: ['', [Validators.required, CustomValidators.cnpj]],
      companyEmail: ['', [Validators.required, Validators.email, CustomValidators.emailNoSpaces]],
      companyPhone: ['', [Validators.required, CustomValidators.phone]],
      bestContactTime: ['', [Validators.required]],
      bestContactMethod: ['', [Validators.required]],
      companyAwareOfReferral: ['', [Validators.required]],
      currentNeeds: [''],
      additionalInformation: ['']
    });
  }

  get referralHeadline(): string | null {
    return this.channelConfigService.referralHeadline;
  }

  get referralSubheadline(): string | null {
    return this.channelConfigService.referralSubheadline;
  }

  get referralButtonLabel(): string | null {
    return this.channelConfigService.referralButtonLabel;
  }

  get referralRegulationUrl(): string | null {
    return this.channelConfigService.referralRegulationUrl;
  }

  get referralBannerUrl(): string | null {
    return this.channelConfigService.referralBannerUrl;
  }

  get referralBannerMobileUrl(): string | null {
    return this.channelConfigService.referralBannerMobileUrl;
  }

  get showDesktopMedia(): boolean {
    return !(this.referralBannerUrl && this.desktopBannerFailed);
  }

  get showMobileMedia(): boolean {
    return !(this.referralBannerMobileUrl && this.mobileBannerFailed);
  }

  get showDesktopBannerImage(): boolean {
    return !!this.referralBannerUrl && !this.desktopBannerFailed;
  }

  get showMobileBannerImage(): boolean {
    return !!this.referralBannerMobileUrl && !this.mobileBannerFailed;
  }

  onDesktopBannerError(): void {
    this.desktopBannerFailed = true;
  }

  onMobileBannerError(): void {
    this.mobileBannerFailed = true;
  }

  ngOnInit(): void {
    // Always reset user pause when entering this page so it behaves like first load
    this.videoState.setPausedByUser(false);

    this.isUtmMode = this.indicationChannelHelper.isChannelMode();
    if (this.isUtmMode) {
      this.indicationChannelHelper.prepareFormForChannelMode(this.indicationForm);
    }

    const initialState = this.indicationForm.get('referrerState')?.value;
    this.selectedState = initialState || '';
    
    const cityControl = this.indicationForm.get('referrerCity');
    if (!this.selectedState) {
      cityControl?.disable();
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.pauseVideosProgrammatically();
      });
  }

  ngAfterViewInit(): void {
    // Desktop video
    const videoEl = this.headerVideo?.nativeElement;
    if (videoEl) {
      videoEl.autoplay = true;
      (videoEl as any).playsInline = true;
      videoEl.volume = 0.3;

      const attemptMutedAutoplay = () => {
        videoEl.muted = true;
        videoEl.setAttribute('muted', '');
        return videoEl.play().then(() => {
          // Show overlay inviting to enable sound
          this.showSoundOverlay = true;
        }).catch(() => {
          this.showSoundOverlay = true;
        });
      };

      // Respect user pause when navigating back
      if (this.videoState.wasPausedByUser()) {
        videoEl.autoplay = false;
        videoEl.pause();
        this.showSoundOverlay = false;
      } else {
        if (document.visibilityState === 'visible' && videoEl.readyState >= 1) {
          attemptMutedAutoplay();
        } else {
          const onLoaded = () => { attemptMutedAutoplay(); };
          const onVisible = () => {
            if (document.visibilityState === 'visible') {
              attemptMutedAutoplay();
              document.removeEventListener('visibilitychange', onVisible);
            }
          };
          videoEl.addEventListener('loadedmetadata', onLoaded, { once: true });
          videoEl.addEventListener('canplay', onLoaded, { once: true });
          videoEl.addEventListener('canplaythrough', onLoaded, { once: true });
          this.visibilityChangeHandler = onVisible;
          document.addEventListener('visibilitychange', onVisible);
        }
      }

      // Track user intent to pause/play
      const onPause: EventListener = () => {
        if (!this.programmaticPause) {
          this.videoState.setPausedByUser(true);
        }
      };
      const onPlay: EventListener = () => {
        this.videoState.setPausedByUser(false);
      };
      videoEl.addEventListener('pause', onPause);
      videoEl.addEventListener('play', onPlay);
      this.desktopVideoListeners.push(['pause', onPause]);
      this.desktopVideoListeners.push(['play', onPlay]);
    }

    // Mobile video
    const mobileVideoEl = this.headerVideoMobile?.nativeElement;
    if (mobileVideoEl) {
      mobileVideoEl.autoplay = true;
      (mobileVideoEl as any).playsInline = true;
      mobileVideoEl.volume = 0.3;
      mobileVideoEl.muted = true; // Mobile starts muted for better UX

      if (this.videoState.wasPausedByUser()) {
        mobileVideoEl.autoplay = false;
        mobileVideoEl.pause();
      }

      // On mobile view, show the overlay to enable sound
      if (window.innerWidth <= 768) {
        this.showSoundOverlay = true;
      }

      const onPauseMobile: EventListener = () => {
        if (!this.programmaticPause) {
          this.videoState.setPausedByUser(true);
        }
      };
      const onPlayMobile: EventListener = () => {
        this.videoState.setPausedByUser(false);
      };
      mobileVideoEl.addEventListener('pause', onPauseMobile);
      mobileVideoEl.addEventListener('play', onPlayMobile);
      this.mobileVideoListeners.push(['pause', onPauseMobile]);
      this.mobileVideoListeners.push(['play', onPlayMobile]);
    }

    this.syncVideos();

    this.addScrollListener();
  }

  startVideoWithSound(): void {
    const videoEl = this.headerVideo?.nativeElement;
    if (!videoEl) return;
    videoEl.muted = false;
    videoEl.removeAttribute('muted');
    videoEl.volume = 0.3;
    videoEl.play().then(() => {
      this.showSoundOverlay = false;
    }).catch(() => {
      this.showSoundOverlay = true;
    });
  }

  startMobileVideoWithSound(): void {
    const mobileVideoEl = this.headerVideoMobile?.nativeElement;
    if (!mobileVideoEl) return;
    mobileVideoEl.muted = false;
    mobileVideoEl.removeAttribute('muted');
    mobileVideoEl.volume = 0.3;
    mobileVideoEl.play().then(() => {
      this.showSoundOverlay = false;
    }).catch(() => {
      this.showSoundOverlay = true;
    });
  }

  onMobileVideoClick(): void {
    const mobileVideoEl = this.headerVideoMobile?.nativeElement;
    if (!mobileVideoEl) return;
    // Toggle play/pause with center click
    if (mobileVideoEl.paused) {
      // Treat click as user intent to play with sound
      this.startMobileVideoWithSound();
    } else {
      this.showSoundOverlay = false; // keep clean UI while playing
      mobileVideoEl.pause();
    }
  }

  onMobileOverlayClick(event: MouseEvent): void {
    event.stopPropagation();
    this.startMobileVideoWithSound();
  }

  closeFloatingVideo(): void {
    this.floatingDismissed = true;
    this.isScrolled = false;
    const mobileVideoEl = this.headerVideoMobile?.nativeElement;
    if (mobileVideoEl) {
      mobileVideoEl.pause();
    }
  }

  private syncVideos(): void {
    const desktopVideo = this.headerVideo?.nativeElement;
    const mobileVideo = this.headerVideoMobile?.nativeElement;

    if (!desktopVideo || !mobileVideo) return;

    const onDesktopPlay: EventListener = () => {
      if (mobileVideo.paused) {
        mobileVideo.currentTime = desktopVideo.currentTime;
        mobileVideo.play().catch(() => {
          // Ignore autoplay rejection while syncing preview videos.
        });
      }
    };
    desktopVideo.addEventListener('play', onDesktopPlay);
    this.desktopVideoListeners.push(['play', onDesktopPlay]);

    const onDesktopPause: EventListener = () => {
      if (!mobileVideo.paused) {
        mobileVideo.pause();
      }
    };
    desktopVideo.addEventListener('pause', onDesktopPause);
    this.desktopVideoListeners.push(['pause', onDesktopPause]);

    const onDesktopSeeked: EventListener = () => {
      if (Math.abs(mobileVideo.currentTime - desktopVideo.currentTime) > 0.5) {
        mobileVideo.currentTime = desktopVideo.currentTime;
      }
    };
    desktopVideo.addEventListener('seeked', onDesktopSeeked);
    this.desktopVideoListeners.push(['seeked', onDesktopSeeked]);

    const onMobilePlay: EventListener = () => {
      if (desktopVideo.paused) {
        desktopVideo.currentTime = mobileVideo.currentTime;
        desktopVideo.play().catch(() => {
          // Ignore autoplay rejection while syncing preview videos.
        });
      }
    };
    mobileVideo.addEventListener('play', onMobilePlay);
    this.mobileVideoListeners.push(['play', onMobilePlay]);

    const onMobilePause: EventListener = () => {
      if (!desktopVideo.paused) {
        desktopVideo.pause();
      }
    };
    mobileVideo.addEventListener('pause', onMobilePause);
    this.mobileVideoListeners.push(['pause', onMobilePause]);

    const onMobileSeeked: EventListener = () => {
      if (Math.abs(desktopVideo.currentTime - mobileVideo.currentTime) > 0.5) {
        desktopVideo.currentTime = mobileVideo.currentTime;
      }
    };
    mobileVideo.addEventListener('seeked', onMobileSeeked);
    this.mobileVideoListeners.push(['seeked', onMobileSeeked]);
  }

  private addScrollListener(): void {
    this.scrollHandler = () => {
      const scrollY = window.scrollY;
      // Only apply floating behavior on mobile
      if (window.innerWidth <= 768) {
        if (this.floatingDismissed) {
          if (this.isScrolled) this.isScrolled = false;
          return;
        }
        if (scrollY > this.scrollThreshold && !this.isScrolled) {
          this.isScrolled = true;
        } else if (scrollY <= this.scrollThreshold && this.isScrolled) {
          this.isScrolled = false;
        }
      }
    };
    window.addEventListener('scroll', this.scrollHandler);
  }

  private pauseAllVideos(): void {
    const desktop = this.headerVideo?.nativeElement;
    const mobile = this.headerVideoMobile?.nativeElement;
    if (desktop && !desktop.paused) desktop.pause();
    if (mobile && !mobile.paused) mobile.pause();
  }

  private pauseVideosProgrammatically(): void {
    this.programmaticPause = true;
    this.pauseAllVideos();
    // Clear flag on next tick so further user pauses are tracked
    setTimeout(() => {
      this.programmaticPause = false;
    }, 0);
  }

  onSubmit(event?: Event): void {
    this.markFormGroupTouched();
    
    const formElement = (event?.target as HTMLFormElement);
    const formValue = this.indicationForm.value;
    const requiredFields = this.indicationChannelHelper.getRequiredFields(this.isUtmMode);
    
    const hasEmptyFields = requiredFields.some(field => !formValue[field]);
    
    if (hasEmptyFields && formElement) {
      setTimeout(() => {
        const firstEmptyField = this.findFirstEmptyRequiredField(formElement, requiredFields);
        if (firstEmptyField) {
          firstEmptyField.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          
          setTimeout(() => {
            firstEmptyField.focus();
          }, 300);
        }
        
        formElement.reportValidity();
      }, 100);
      return;
    }

    if (formElement && !formElement.checkValidity()) {
      setTimeout(() => {
        const firstInvalidField = formElement.querySelector(':invalid') as HTMLElement;
        if (firstInvalidField) {
          firstInvalidField.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });

          setTimeout(() => {
            firstInvalidField.focus();
          }, 300);
        }
        
        formElement.reportValidity();
      }, 100);
      return;
    }

    if (!this.isSubmitting) {
      this.performSubmitWithCityValidation();
    }
  }

  onSubmitIndication(): void {
    if (this.indicationForm.valid && !this.isSubmitting) {
      this.performSubmitWithCityValidation();
    } else {
      this.markFormGroupTouched();
    }
  }

  private async performSubmitWithCityValidation(): Promise<void> {
    if (this.isSubmitting) return;

    this.indicationForm.markAllAsTouched();
    if (this.indicationForm.invalid) {
      this.toastService.showError(
        this.i18nService.instant('pages.indication.messages.formError'), 
        3000, 
        'top-center'
      );
      return;
    }

    this.isSubmitting = true;

    try {
      if (!this.isUtmMode) {
        const stateValue = this.indicationForm.get('referrerState')?.value;
        const cityValue = this.indicationForm.get('referrerCity')?.value;

        if (stateValue && cityValue) {
          const isValidCity = await this.validateCityInState(stateValue, cityValue);
          if (!isValidCity) {
            this.isSubmitting = false;
            return;
          }
        }

        this.submitToEndpoint(this.mapFormDataToEndpoint());
        return;
      }

      this.submitChannelIndication(
        this.indicationChannelHelper.buildRequest(this.indicationForm.value)
      );
    } catch (error) {
      console.error('Erro na validação da cidade:', error);
      this.isSubmitting = false;
      this.toastService.showError(
        this.i18nService.instant('pages.indication.messages.validationError'),
        5000,
        'top-center'
      );
    }
  }

  private async validateCityInState(stateCode: string, cityName: string): Promise<boolean> {
    try {
      let cityNames: string[];
      
      if (this.cities.length > 0 && this.selectedState === stateCode) {
        cityNames = this.cities.map(city => city.toLowerCase());
      } else {
        const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;
        const cities = await this.http.get<IBGECity[]>(apiUrl).toPromise();
        
        if (!cities) {
          this.toastService.showError(
            this.i18nService.instant('pages.indication.messages.cityError'), 
            3000, 
            'top-center'
          );
          return false;
        }
        
        cityNames = cities.map(city => city.nome.toLowerCase());
      }
      const normalizedInput = cityName.toLowerCase().trim();
      const cityExists = cityNames.includes(normalizedInput);
      
      if (!cityExists) {
        const cityControl = this.indicationForm.get('referrerCity');
        if (cityControl) {
          cityControl.setErrors({ 'invalidCity': true });
          cityControl.markAsTouched();
        }
        
        setTimeout(() => {
          const cityField = document.getElementById('referrerCity') as HTMLInputElement;
          if (cityField) {
            cityField.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            cityField.focus();
          }
        }, 100);
        
        this.toastService.showError(
          this.i18nService.instant('pages.indication.messages.cityNotFound', { city: cityName }), 
          3000, 
          'top-center'
        );
        return false;
      }

      const cityControl = this.indicationForm.get('referrerCity');
      if (cityControl && cityControl.hasError('invalidCity')) {
        const errors = { ...cityControl.errors };
        delete errors['invalidCity'];
        cityControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }

      return true;
    } catch (error) {
      console.error('Erro ao validar cidade:', error);
      this.toastService.showError(
        this.i18nService.instant('pages.indication.messages.cityError'), 
        3000, 
        'top-center'
      );
      return false;
    }
  }

  private mapFormDataToEndpoint(): IndicationRequest {
    const formValue = this.indicationForm.value;
    
    return {
      employee: {
        name: formValue.referrerName,
        phone: this.removePhoneMask(formValue.referrerPhone),
        sector: formValue.referrerArea,
        email: formValue.referrerEmail,
        city: formValue.referrerCity,
        state: formValue.referrerState
      },
      indication: {
        name: formValue.contactName,
        companyName: formValue.companyName,
        document: this.removeCNPJMask(formValue.companyCNPJ),
        phone: this.removePhoneMask(formValue.companyPhone), 
        email: formValue.companyEmail
      },
      complementaryData: {
        bestContactTime: formValue.bestContactTime || '',
        contactMethod: formValue.bestContactMethod || '',
        additionalInfo: formValue.additionalInformation || '',
        companyAware: formValue.companyAwareOfReferral === 'sim'
      }
    };
  }

  private removePhoneMask(phone: string): string {
    return phone ? phone.replace(/\D/g, '') : '';
  }

  private removeCNPJMask(cnpj: string): string {
    return unformatCnpj(cnpj);
  }

  private submitToEndpoint(data: IndicationRequest): void {
    this.indicationService.submitIndication(data).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => this.handleIndicationSuccess(response),
      error: (error) => this.handleIndicationError(error),
    });
  }

  private submitChannelIndication(data: ChannelIndicationRequest): void {
    this.indicationService.submitChannelIndication(data).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (httpResponse) =>
        this.handleIndicationSuccess(httpResponse.body, httpResponse.status),
      error: (error) => this.handleIndicationError(error),
    });
  }

  private handleIndicationSuccess(
    response: { status?: string; msg?: string; uuid?: string } | null,
    httpStatus?: number
  ): void {
    const isSuccess =
      httpStatus === 201 ||
      response?.status === 'success' ||
      !!response?.uuid;

    if (isSuccess) {
      this.toastService.showSuccess(
        this.i18nService.instant('pages.indication.messages.success'),
        3000,
        'top-center'
      );

      this.isSubmitting = false;
      this.resetForm();
    } else {
      const errorMessage =
        response?.msg || this.i18nService.instant('pages.indication.messages.processError');
      this.toastService.showError(errorMessage, 5000, 'top-center');
      this.isSubmitting = false;
    }
  }

  private handleIndicationError(error: any): void {
    console.error('Erro ao enviar indicação:', error);

    let errorMessage = this.i18nService.instant('pages.indication.messages.connectionError');

    if (error.status === 400) {
      errorMessage = this.i18nService.instant('pages.indication.messages.invalidData');
    } else if (error.error && error.error.msg) {
      errorMessage = error.error.msg;
    }

    this.toastService.showError(errorMessage, 5000, 'top-center');
    this.isSubmitting = false;
  }

  onReset(): void {
    this.resetForm();
  }

  private resetForm(): void {
    this.indicationForm.reset({
      referrerState: '',
      bestContactMethod: '',
      companyAwareOfReferral: ''
    });
  }

  private findFirstEmptyRequiredField(formElement: HTMLFormElement, requiredFields: string[]): HTMLElement | null {
    for (const fieldName of requiredFields) {
      const fieldElement = formElement.querySelector(`#${fieldName}`) as HTMLElement;
      if (fieldElement && !this.indicationForm.get(fieldName)?.value) {
        return fieldElement;
      }
    }
    return null;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.indicationForm.controls).forEach(key => {
      const control = this.indicationForm.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.indicationForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.indicationForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return 'Esse campo é obrigatório';
      }
      if (field.errors['email']) {
        return 'Por favor, insira um endereço de email válido';
      }
      if (field.errors['emailWithSpaces']) {
        return 'O email não pode conter espaços';
      }
      if (field.errors['invalidCorporateEmail']) {
        return 'Email corporativo inválido';
      }
      if (field.errors['invalidCity']) {
        return 'Cidade não encontrada para o estado selecionado';
      }
      if (field.errors['invalidFullName']) {
        return 'Digite nome e sobrenome completos';
      }
      if (field.errors['invalidPhone']) {
        return 'Telefone inválido. Digite apenas os números';
      }
      if (field.errors['invalidCNPJ']) {
        return 'CNPJ inválido. Verifique os números digitados';
      }
      if (field.errors['whitespaceOnly']) {
        return 'Este campo não pode conter apenas espaços';
      }
    }
    return '';
  }

  get referrerNameControl() {
    return this.indicationForm.get('referrerName');
  }

  get referrerEmailControl() {
    return this.indicationForm.get('referrerEmail');
  }

  get referrerPhoneControl() {
    return this.indicationForm.get('referrerPhone');
  }

  get referrerAreaControl() {
    return this.indicationForm.get('referrerArea');
  }

  get referrerCityControl() {
    return this.indicationForm.get('referrerCity');
  }

  get referrerStateControl() {
    return this.indicationForm.get('referrerState');
  }

  get contactNameControl() {
    return this.indicationForm.get('contactName');
  }

  get companyNameControl() {
    return this.indicationForm.get('companyName');
  }

  get companyCNPJControl() {
    return this.indicationForm.get('companyCNPJ');
  }

  get companyEmailControl() {
    return this.indicationForm.get('companyEmail');
  }

  get companyPhoneControl() {
    return this.indicationForm.get('companyPhone');
  }

  get bestContactTimeControl() {
    return this.indicationForm.get('bestContactTime');
  }

  get bestContactMethodControl() {
    return this.indicationForm.get('bestContactMethod');
  }

  get companyAwareOfReferralControl() {
    return this.indicationForm.get('companyAwareOfReferral');
  }

  get additionalInformationControl() {
    return this.indicationForm.get('additionalInformation');
  }

  // Aplicar máscara de telefone 
  applyPhoneMask(event: any): void {
    const input = event.target;
    const cursorPosition = input.selectionStart;
    const inputType = event.inputType;

    let value = input.value.replace(/\D/g, '');
    
    if (value.length > 11) {
      value = value.substring(0, 11);
    }
    
    let formattedValue = '';
    
    if (value.length >= 2) {
      const ddd = value.substring(0, 2);
      const phoneNumber = value.substring(2);
      
      if (phoneNumber.length === 0) {
        formattedValue = `(${ddd}`;
      } else if (phoneNumber.length <= 4) {
        formattedValue = `(${ddd}) ${phoneNumber}`;
      } else if (phoneNumber.length === 5) {
        formattedValue = `(${ddd}) ${phoneNumber}`;
      } else if (phoneNumber.length <= 8) {
        const firstPart = phoneNumber.substring(0, 4);
        const secondPart = phoneNumber.substring(4);
        formattedValue = `(${ddd}) ${firstPart}-${secondPart}`;
      } else {
        const firstPart = phoneNumber.substring(0, 5);
        const secondPart = phoneNumber.substring(5);
        formattedValue = `(${ddd}) ${firstPart}-${secondPart}`;
      }
    } else if (value.length === 1) {
      formattedValue = `(${value}`;
    } else {
      formattedValue = '';
    }
    
    input.value = formattedValue;
    
    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.indicationForm.get(controlName)?.setValue(formattedValue);
    }
    
    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      let newCursorPosition = cursorPosition;
      
      if (newCursorPosition > 0 && newCursorPosition <= formattedValue.length) {
        const charAtCursor = formattedValue.charAt(newCursorPosition - 1);
        if (charAtCursor === '(' || charAtCursor === ')' || charAtCursor === ' ' || charAtCursor === '-') {
          newCursorPosition = Math.max(0, newCursorPosition - 1);
        }
      }
      
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  }

  onPhoneKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const value = input.value;

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Insert'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length >= 11) {
      event.preventDefault();
      return;
    }
  }

  // Aplicar máscara de CNPJ (numérico ou alfanumérico)
  applyCNPJMask(event: any): void {
    const input = event.target;
    if (event.inputType === 'deleteContentBackward') {
      return;
    }
    const originalCursorPos = input.selectionStart;
    const value = formatCnpj(input.value);
    input.value = value;
    const newCursorPos = Math.min(originalCursorPos, value.length);
    setTimeout(() => {
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);

    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.indicationForm.get(controlName)?.setValue(value);
    }
  }

  onClick(event: any): void {
    const formElement = document.querySelector('.form-section');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  onRegulamentClick(event: any): void {
    const url = this.referralRegulationUrl ?? DEFAULT_INDICATION_REGULATION_URL;
    window.open(url, '_blank');
  }

  onStateChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const stateValue = target.value;
    this.selectedState = stateValue;
    
    const cityControl = this.indicationForm.get('referrerCity');
    
    cityControl?.setValue('');
    this.showCitySuggestions = false;
    this.filteredCities = [];
    
    if (stateValue) {
      cityControl?.enable();
      this.loadCitiesFromIBGE(stateValue);
    } else {
      cityControl?.disable();
      this.cities = [];
    }
  }

  loadCitiesFromIBGE(stateCode: string): void {
    this.isLoadingCities = true;
    const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;
    
    this.http.get<IBGECity[]>(apiUrl).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.cities = data.map(city => city.nome).sort();
        this.isLoadingCities = false;
      },
      error: (error) => {
        console.error('Erro ao carregar cidades:', error);
        this.cities = [];
        this.isLoadingCities = false;
        this.toastService.showError(
          this.i18nService.instant('pages.indication.messages.cityLoadError'), 
          3000, 
          'top-center'
        );
      }
    });
  }

  getCityPlaceholder(): string {
    if (!this.selectedState) {
      return 'pages.indication.section1.city.placeholderDefault'; 
    }
    return 'pages.indication.section1.city.placeholderActive';
  }

  onCityInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    
    if (!value || value.length < 1) {
      this.showCitySuggestions = false;
      this.filteredCities = [];
      return;
    }

    this.filteredCities = this.cities.filter(city =>
      city.toLowerCase().includes(value.toLowerCase())
    ).slice(0, 10); // Limita a 10 sugestões

    this.showCitySuggestions = this.filteredCities.length > 0;
    this.selectedSuggestionIndex = -1;
  }

  onCityFocus(): void {
    const currentValue = this.indicationForm.get('referrerCity')?.value;
    if (currentValue && this.cities.length > 0) {
      this.onCityInput({ target: { value: currentValue } } as any);
    }
  }

  onCityKeydown(event: KeyboardEvent): void {
    if (!this.showCitySuggestions || this.filteredCities.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.min(
          this.selectedSuggestionIndex + 1,
          this.filteredCities.length - 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedSuggestionIndex >= 0) {
          this.selectCity(this.filteredCities[this.selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        this.showCitySuggestions = false;
        this.selectedSuggestionIndex = -1;
        break;
    }
  }

  selectCity(city: string): void {
    this.indicationForm.get('referrerCity')?.setValue(city);
    this.showCitySuggestions = false;
    this.filteredCities = [];
    this.selectedSuggestionIndex = -1;
  }

  /** Não deixar escrever espaço no primeiro caractere */
  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault(); // 🚫 não deixa digitar espaço no começo
    }
  }

  ngOnDestroy(): void {
    this.pauseAllVideos();

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = undefined;
    }

    const desktopVideo = this.headerVideo?.nativeElement;
    const mobileVideo = this.headerVideoMobile?.nativeElement;
    if (desktopVideo) {
      for (const [event, handler] of this.desktopVideoListeners) {
        desktopVideo.removeEventListener(event, handler);
      }
      this.desktopVideoListeners = [];
    }
    if (mobileVideo) {
      for (const [event, handler] of this.mobileVideoListeners) {
        mobileVideo.removeEventListener(event, handler);
      }
      this.mobileVideoListeners = [];
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
